/**
 * Pure logic behind the agent connectivity self-test ("Test my connection",
 * beads beads_php_master_phone_system-6vm). Kept apart from
 * `components/ConnectionTest.tsx` so the two failure-prone browser APIs it
 * touches — `getUserMedia` and the Web Audio `AnalyserNode` — can be mocked
 * directly in tests without rendering React at all.
 *
 * A "Ready" pill in the console only ever proved SIP registration. Nothing
 * in this codebase before this file checked that a microphone exists, that
 * the browser was allowed to use it, or that audio actually flows in either
 * direction. This is that check, in two stages:
 *
 *   1. checkMicrophone() — permission + device existence, via getUserMedia.
 *   2. runEchoCallStage() — an actual SIP call through the Softphone to a
 *      dialplan echo extension, proving the full DTLS-SRTP media path in
 *      both directions (the agent's own voice loops back to them).
 *
 * Both return a plain-English remedy string on failure rather than a raw
 * browser error — see the REMEDY_* constants, which are the exact strings
 * beads_php_master_phone_system-6vm's description asks for.
 */
import type { Softphone, SoftphoneCallbacks, PhoneState } from "./softphone";
import type { SipCredentials } from "../api/agent";

export const REMEDY_MIC_BLOCKED = "Your browser blocked the microphone";
export const REMEDY_NO_MIC = "No microphone detected";
export const REMEDY_NO_SERVER = "Can't reach the phone server";
export const REMEDY_NETWORK_AUDIO = "Audio is blocked by your network";
/**
 * Audio reached this browser and decoded — and the device played none of it.
 *
 * A distinct outcome from REMEDY_NETWORK_AUDIO, and the distinction is the
 * whole point: the network is fine, the call is fine, the far end can hear the
 * agent normally. Only the speaker is silent, and no amount of network
 * troubleshooting touches it.
 *
 * This case used to pass the test. The level meter below reads the decoded
 * MediaStream through WebAudio, which is completely independent of the
 * `<audio>` element that actually makes sound, so a phone emitting nothing
 * still drove the meter to full deflection and scored a green "audio is live".
 * That is exactly what an operator saw on 2026-08-09 while hearing silence for
 * a whole 35-second call.
 */
export const REMEDY_NO_PLAYBACK =
  "Your device isn't playing call audio — tap Turn on call audio, and check your ringer/silent switch";
/** The console's own softphone genuinely has a live call up (a real session
 * backing the "ringing"/"on-call" state — see Softphone.hasActiveSession())
 * — refused rather than risking an interruption of that real, in-progress
 * call. Never shown for a merely stale state value with no session behind
 * it; see ConnectionTest.tsx's `reusableLiveSoftphone`. */
export const REMEDY_BUSY = "Finish your current call, then run the test again.";
/** The console's softphone is still (genuinely) registering — a distinct
 * remedy from REMEDY_BUSY so the footer never claims "you're on a call"
 * while the console itself is showing "Connecting" (beads
 * beads_php_master_phone_system-0uf's sibling finding: the copy must match
 * what the agent actually sees). */
export const REMEDY_STILL_CONNECTING = "Still connecting — wait a moment, then run the test again.";

/** Dialplan extension the echo/loopback test call dials — see
 * deploy/asterisk/extensions_ictbroadcast.conf's `[ictbroadcast-agent-outbound]`
 * context, `*43`. Every browser-originated call from an agent's own seat
 * lands in that context regardless of what it dials (see that file's
 * docstring), so this can never reach a real campaign or a customer. */
export const ECHO_TEST_TARGET = "*43";

/** How long the echo call is allowed to take to reach "on-call" (registration
 * + INVITE + full ICE/DTLS-SRTP negotiation) before the media-path stage is
 * scored a failure. Generous because ICE gathering over a slow network is
 * exactly one of the things this stage exists to catch. */
export const ECHO_CALL_TIMEOUT_MS = 8000;

/**
 * How long the echo call is left connected, unattended, before this side
 * auto-hangs-up as a safety cap — deliberately much longer than
 * ECHO_CALL_TIMEOUT_MS. That timeout only bounds how long the call is given
 * to REACH "on-call"; once it's up, the whole point (see runEchoCallStage /
 * runEchoCallOnLiveSoftphone doc comments) is that it STAYS connected so an
 * agent can see and use the "on call" controls (Hang up / Mute / Transfer)
 * rather than watching it vanish the instant the audio check passes. This is
 * just the backstop for a forgotten tab: generous enough that it never cuts
 * off an agent still looking at the card, short enough that a test call can
 * never squat on a seat indefinitely.
 *
 * The dialplan's own `TIMEOUT(absolute)` on the `*43` extension (see
 * deploy/asterisk/extensions_ictbroadcast.conf) is set well ABOVE this value
 * on purpose — Asterisk's cap exists as the true last-resort backstop should
 * the browser tab die outright, not as the everyday way this call ends. The
 * client is meant to always be the one that hangs up first.
 */
export const TEST_CALL_IDLE_TIMEOUT_MS = 120_000;

/** How long the mic-level meter samples before the self-test auto-advances
 * to the call stage. Long enough for an agent to see the bar move if they
 * say something, short enough that the whole test stays a "run it and wait a
 * few seconds" affordance rather than a chore. */
export const MIC_METER_DURATION_MS = 3000;
export const METER_SAMPLE_INTERVAL_MS = 100;

export type StageStatus = "pending" | "running" | "pass" | "fail";

export interface StageResult {
  status: StageStatus;
  remedy?: string;
}

// ---------------------------------------------------------------------------
// Stage 1 — microphone permission + device existence
// ---------------------------------------------------------------------------

export type MicCheckResult =
  | { ok: true; stream: MediaStream }
  | { ok: false; remedy: string };

/**
 * Request microphone access and confirm a live audio track came back.
 * Never throws — every failure is translated to one of the plain-English
 * REMEDY_* strings so the UI never has to sniff a DOMException name itself.
 */
export async function checkMicrophone(
  mediaDevices: Pick<MediaDevices, "getUserMedia"> | undefined = typeof navigator !== "undefined"
    ? navigator.mediaDevices
    : undefined,
): Promise<MicCheckResult> {
  if (!mediaDevices?.getUserMedia) {
    return { ok: false, remedy: REMEDY_NO_MIC };
  }
  let stream: MediaStream;
  try {
    stream = await mediaDevices.getUserMedia({ audio: true });
  } catch (err) {
    const name = err instanceof Error ? err.name : "";
    // NotFoundError/OverconstrainedError: no device exists at all — a
    // different fix (plug in a headset) than a browser permission the agent
    // can flip back on, so it gets its own remedy.
    if (name === "NotFoundError" || name === "OverconstrainedError") {
      return { ok: false, remedy: REMEDY_NO_MIC };
    }
    return { ok: false, remedy: REMEDY_MIC_BLOCKED };
  }
  if (stream.getAudioTracks().length === 0) {
    stream.getTracks().forEach((t) => t.stop());
    return { ok: false, remedy: REMEDY_NO_MIC };
  }
  return { ok: true, stream };
}

// ---------------------------------------------------------------------------
// Live level meter — shared by the mic check (stage 1) and, best-effort, the
// echoed remote stream during the call (stage 2), so the agent sees their
// own voice move a bar in both places rather than just trusting a pass/fail
// pill.
// ---------------------------------------------------------------------------

export interface LevelMeter {
  /** Instantaneous RMS level, 0..1. */
  getLevel: () => number;
  stop: () => void;
}

/**
 * Wrap `stream` in a WebAudio AnalyserNode and expose its current RMS level.
 *
 * Returns null (never throws) when the Web Audio API is unavailable —
 * jsdom/older browsers have no AudioContext at all. The meter is a visual
 * aid, not part of the pass/fail decision, so its absence must not fail a
 * stage that otherwise succeeded.
 */
export function createLevelMeter(stream: MediaStream): LevelMeter | null {
  const Ctor = (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
    .AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;

  const ctx = new Ctor();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  source.connect(analyser);
  const data = new Uint8Array(analyser.frequencyBinCount);

  return {
    getLevel: () => {
      analyser.getByteTimeDomainData(data);
      let sumSquares = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sumSquares += v * v;
      }
      return Math.sqrt(sumSquares / data.length);
    },
    stop: () => {
      try {
        source.disconnect();
      } catch {
        /* already torn down — fine */
      }
      try {
        void ctx.close();
      } catch {
        /* already closed — fine */
      }
    },
  };
}

/**
 * Sample `meter` every METER_SAMPLE_INTERVAL_MS for `durationMs`, reporting
 * each level to `onLevel`. Resolves once the window elapses; never rejects.
 * A null meter (Web Audio unavailable) resolves immediately with no samples
 * — the caller still gets its elapsed-time behaviour, just no visual.
 */
export function sampleMeterFor(
  meter: LevelMeter | null,
  durationMs: number,
  onLevel: (level: number) => void,
): Promise<void> {
  if (!meter) return Promise.resolve();
  return new Promise((resolve) => {
    const start = Date.now();
    const id = setInterval(() => {
      onLevel(meter.getLevel());
      if (Date.now() - start >= durationMs) {
        clearInterval(id);
        resolve();
      }
    }, METER_SAMPLE_INTERVAL_MS);
  });
}

// ---------------------------------------------------------------------------
// Stage 2 — media path: register, place the echo call, prove it establishes
// ---------------------------------------------------------------------------

export interface MediaStageResult extends StageResult {
  /** Whether the temporary softphone registered at all — surfaced in the
   * report (beads 6vm's stage 3) so an agent can tell support exactly what
   * they saw, distinct from "registered fine, but the call never connected". */
  registered: boolean;
}

export interface MediaStageDeps {
  /** Fresh Softphone instance for the test — never the agent's own live
   * console registration, so the self-test can run at any time, including
   * before the agent has gone online (beads 6vm: "any time, especially
   * before going available"). */
  softphone: Softphone;
  audio: HTMLAudioElement;
  getSipCredentials: (extensionId: number) => Promise<SipCredentials>;
  extensionId: number;
  onRemoteLevel?: (level: number) => void;
  timeoutMs?: number;
}

/**
 * Register a temporary softphone session and place a call to the echo
 * extension, resolving once the call either establishes or the attempt is
 * given up on.
 *
 * Three distinct failure shapes, each with its own remedy:
 *   - credentials/registration never succeed -> "Can't reach the phone server"
 *   - registered, but the call never reaches "on-call" within the timeout ->
 *     "Audio is blocked by your network"
 *   - registered AND established -> pass
 *
 * Always tears down the temporary session before returning, regardless of
 * outcome — this must never leave a channel open on the echo extension.
 */
export async function runEchoCallStage(deps: MediaStageDeps): Promise<MediaStageResult> {
  const { softphone, audio, getSipCredentials, extensionId, onRemoteLevel, timeoutMs } = deps;
  const timeout = timeoutMs ?? ECHO_CALL_TIMEOUT_MS;

  let credentials: SipCredentials;
  try {
    credentials = await getSipCredentials(extensionId);
  } catch {
    return { status: "fail", remedy: REMEDY_NO_SERVER, registered: false };
  }

  let registered = false;
  // Wired up BEFORE call() is ever invoked (not inside a promise executor
  // created after awaiting it) so a delegate event firing on the very next
  // microtask after call() resolves — which is exactly how a fast dialplan
  // answer behaves — can never be missed. Doing this the other way around
  // was a real race: awaiting call() first and only then constructing the
  // "wait for on-call" promise left a window where onCallResolve was still
  // null when the event arrived.
  let onCallResolve: ((established: boolean) => void) | null = null;
  const established = new Promise<boolean>((resolve) => {
    onCallResolve = resolve;
  });
  // Resolved off the SAME delegate stream, and for the same reason
  // `established` is: registration is reported by a callback, never by the
  // resolution of the method that starts it. See the connect race below.
  let onRegisteredResolve: ((ok: boolean) => void) | null = null;
  const registrationSettled = new Promise<boolean>((resolve) => {
    onRegisteredResolve = resolve;
  });
  const callbacks: SoftphoneCallbacks = {
    onState: (state: PhoneState) => {
      if (state === "idle") {
        registered = true;
        onRegisteredResolve?.(true);
      }
      if (state === "on-call") onCallResolve?.(true);
      if (state === "offline") {
        onRegisteredResolve?.(false);
        onCallResolve?.(false);
      }
    },
    onIncoming: () => {
      /* the echo extension never calls back — nothing to answer */
    },
    onError: () => {
      onRegisteredResolve?.(false);
      onCallResolve?.(false);
    },
  };

  // Bounded, deliberately: `softphone.connect()` awaits the underlying
  // WebSocket transport opening, and a network/firewall that silently drops
  // (rather than rejects) traffic to the phone server's WSS port leaves that
  // promise neither resolving nor rejecting — verified live against prod
  // from a network without a path to it: the WebSocket sat in CONNECTING
  // with no open/error/close event at all. Without this race, that is
  // exactly the failure this stage exists to catch (a network blocking the
  // phone server) hanging the self-test in "Testing…" forever instead of
  // reporting REMEDY_NO_SERVER.
  // ONE deadline spanning connect AND the registration it is waiting on,
  // armed before connect() starts so a stuck transport and a never-answered
  // REGISTER are bounded by the same budget.
  let registerTimer: ReturnType<typeof setTimeout> | undefined;
  const registerDeadline = new Promise<"timeout">((resolve) => {
    registerTimer = setTimeout(() => resolve("timeout"), timeout);
  });

  const connectOutcome = await Promise.race([
    softphone
      .connect(credentials, audio, callbacks)
      // `connect()` resolves once the REGISTER has been SENT — NOT once
      // Asterisk has accepted it. Softphone.connect() says so itself: it
      // deliberately fires no `onState("idle")` of its own, because that
      // state only ever comes from the `onRegistered` delegate, on the 200 OK
      // (beads-209). So reading `registered` straight off this resolution was
      // a race the client could never win — the resolution and the read are
      // microtasks apart, while the 200 OK is a whole network round trip
      // away, so `registered` was still false EVERY time and a perfectly
      // healthy phone server scored REMEDY_NO_SERVER ("Can't reach the phone
      // server"). It went unnoticed because the unit test's fake softphone
      // fires `onState("idle")` synchronously from inside its `connect()`
      // mock, which no real registration can do. Wait for the delegate to
      // actually report the registration instead of inferring it.
      .then(() => registrationSettled)
      .then((ok) => (ok ? ("registered" as const) : ("failed" as const)))
      .catch(() => "failed" as const),
    registerDeadline,
  ]);
  clearTimeout(registerTimer);

  if (connectOutcome !== "registered") {
    await softphone.disconnect();
    return { status: "fail", remedy: REMEDY_NO_SERVER, registered };
  }

  try {
    await softphone.call(ECHO_TEST_TARGET);
  } catch {
    await softphone.disconnect();
    return { status: "fail", remedy: REMEDY_NO_SERVER, registered: true };
  }

  const timeoutHandle = setTimeout(() => onCallResolve?.(false), timeout);
  const establishedResult = await established;
  clearTimeout(timeoutHandle);

  if (establishedResult && onRemoteLevel) {
    const remote = softphone.remoteStream();
    if (remote) {
      const meter = createLevelMeter(remote);
      await sampleMeterFor(meter, Math.min(2000, timeout), onRemoteLevel);
      meter?.stop();
    }
  }

  if (!establishedResult) {
    try {
      await softphone.hangup();
    } catch {
      /* best-effort — disconnect() below tears the session down regardless */
    }
    await softphone.disconnect();
    return { status: "fail", remedy: REMEDY_NETWORK_AUDIO, registered: true };
  }

  // Established: leave the call UP rather than tearing it down here — the
  // whole point of this change is that a passed test stays on screen with
  // working Hang up / Mute / Transfer controls instead of vanishing the
  // instant the audio check completes (see TEST_CALL_IDLE_TIMEOUT_MS's doc
  // comment). This temporary instance has no UI control of its own to end
  // the call later (unlike the live-instance reuse path below, whose caller
  // owns that via the console's own Hang up button), so it is fully
  // self-contained here: arm a fire-and-forget safety-cap teardown and
  // return immediately without awaiting it.
  setTimeout(() => {
    void softphone
      .hangup()
      .catch(() => {
        /* already ended some other way — nothing to clean up */
      })
      .finally(() => {
        void softphone.disconnect();
      });
  }, TEST_CALL_IDLE_TIMEOUT_MS);

  // Same playback assertion as the live-instance path below: audio arriving
  // and audio being audible are two different claims, and only the second is
  // what the agent is about to rely on. Deliberately AFTER the safety-cap
  // timer is armed — this temporary instance has no UI able to end its call,
  // so returning before that timer exists would strand a live session and a
  // registration with nothing left holding a reference to them.
  if (softphone.playbackBlocked()) {
    return { status: "fail", remedy: REMEDY_NO_PLAYBACK, registered: true };
  }

  return { status: "pass", registered: true };
}

// ---------------------------------------------------------------------------
// Stage 2, alternate path — reuse the agent's OWN already-registered
// softphone instead of a temporary one.
//
// `pjsip_webrtc.conf`'s `[webrtc-agent]` template is explicit:
// `max_contacts = 1` / `remove_existing = yes` — a second REGISTER for the
// same seat evicts the first. `GET /api/agent/sip-credentials` returns that
// seat's one static secret every time, so a temporary Softphone connecting
// with the SAME credentials while the agent's own console is already
// registered does not run a harmless side-by-side test: it silently steals
// the agent's real registration. Softphone's own generation guard only
// protects re-entrancy within ONE instance; it does nothing across two
// separate instances authenticating as the same AOR.
//
// `runEchoCallOnLiveSoftphone` is the fix: reuse the console's live instance
// and touch ONLY `.call()`/`.hangup()` — never `.connect()`/`.disconnect()`,
// the only two methods that touch registration. That is a structural
// guarantee (an omission, not a runtime check), and it is also a BETTER
// test than a temporary instance: it exercises the exact registration the
// agent is about to take real calls on.
// ---------------------------------------------------------------------------

export interface LiveCallStageDeps {
  /** The agent's OWN already-registered softphone (from AgentConsole, threaded
   * down through AgentPanelPage) — never connected or disconnected here. */
  softphone: Softphone;
  onRemoteLevel?: (level: number) => void;
  /**
   * Resolves true once the reused call reaches "on-call", false if it gives
   * up. Supplied by the caller because Softphone allows only ONE delegate at
   * a time, already wired to the console's own callbacks by its earlier
   * `connect()` — this function cannot attach a second listener to observe
   * the call it just placed, so it relies on the SAME state stream the
   * console itself already reports, one level up (ConnectionTest watches the
   * `livePhoneState` prop it already receives from AgentPanelPage).
   */
  waitForOnCall: () => Promise<boolean>;
  target?: string;
}

export async function runEchoCallOnLiveSoftphone(
  deps: LiveCallStageDeps,
): Promise<MediaStageResult> {
  const { softphone, onRemoteLevel, waitForOnCall } = deps;
  const target = deps.target ?? ECHO_TEST_TARGET;

  try {
    await softphone.call(target);
  } catch {
    // SimpleUser.call() rejects if a session already exists — the console
    // was busy after all (a race between the caller's own "idle" read and
    // this call). Not a server-reachability problem, but there is no
    // registration to blame either; REMEDY_NO_SERVER is the closest existing
    // remedy and `registered: true` keeps the report line accurate.
    return { status: "fail", remedy: REMEDY_NO_SERVER, registered: true };
  }

  const establishedResult = await waitForOnCall();

  if (establishedResult && onRemoteLevel) {
    const remote = softphone.remoteStream();
    if (remote) {
      const meter = createLevelMeter(remote);
      await sampleMeterFor(meter, 2000, onRemoteLevel);
      meter?.stop();
    }
  }

  if (!establishedResult) {
    try {
      await softphone.hangup();
    } catch {
      // Best-effort. Deliberately no disconnect() fallback here (unlike the
      // temporary-instance path above) — this function never opened the
      // registration, so there is nothing further to tear down; the
      // console's own delegate handles a stuck/dropped call exactly as it
      // would for any other call.
    }
    return { status: "fail", remedy: REMEDY_NETWORK_AUDIO, registered: true };
  }

  // The call is up and the stream carries audio — now the question the meter
  // above cannot answer: did this device play any of it? Checked last so it
  // can only ever downgrade a test that has otherwise passed. The call is left
  // up either way; a silent speaker is the agent's to fix, not grounds to
  // hang up on them.
  if (softphone.playbackBlocked()) {
    return { status: "fail", remedy: REMEDY_NO_PLAYBACK, registered: true };
  }

  // Established: deliberately does NOT hang up here. Unlike the temporary
  // instance above, this softphone is the agent's OWN live registration —
  // it is reused for every subsequent real call too, so this function must
  // never arm its own delayed hangup() the way runEchoCallStage does (a
  // stray timer firing after the agent has moved on to a genuine customer
  // call would hang up on the CUSTOMER). Ending the call is therefore the
  // caller's job: the agent's own Hang up button (already wired to this
  // exact softphone instance in AgentConsole) for the normal path, and
  // ConnectionTest's own TEST_CALL_IDLE_TIMEOUT_MS safety-cap timer — armed
  // and, critically, CANCELLED the instant the console's phoneState leaves
  // "on-call" for any reason — for the forgotten-tab path. See
  // ConnectionTest.tsx's runTest().
  return { status: "pass", registered: true };
}
